import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StatusCode } from "api/protocols";
import { GetFloorPlans, GetTable, GetTableElements, ITableElement } from "api/rpc/2024-04/facilityAdmin/tables/tables";
import { IFloorPlan, ITable } from "api/rpc/facilityAdmin/tables/tables";
import axios, { CancelToken } from "axios";
import { ButtonNew as Button} from "components/buttonNew";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import SelectNew from "components/select/SelectNew";
import { isEmpty } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { showError } from "redux/actions/ui";
import "./reservationTableSelection.scss";
import Checkbox from "components/form/checkbox/Checkbox";
import classNames from "classnames";
import Spin from "components/spin/spin";
import Callout from "components/callout/Callout";

interface IProps {
    guestsAmount: number;
    onTableSelect: (tableId: number, seatsAmount: number) => void;
    selectedTableId: number;
}

interface IState {
    floorPlans: Array<IFloorPlan>;
    selectedFloorPlan: IFloorPlan;
    tables: Array<ITable>;
    tableElements: ITableElement[];
    seats: string;
}

export default function ReservationTableSelection(props: IProps) {
    const { guestsAmount, onTableSelect, selectedTableId } = props;
    const dispatch = useAppDispatch();
    const { facilityStore } = useAppSelector(store => store);
    const { t } = useTranslation();
    const [state, setState] = useState<IState>({
        floorPlans: undefined,
        selectedFloorPlan: null,
        tables: undefined,
        tableElements: [],
        seats: "1",
    });

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (state.floorPlans === undefined) {
            void getFloorsPlans(source.token);
        }
        return () => source.cancel();
    }, [state.floorPlans]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (state?.selectedFloorPlan?.id) {
            void getTables(source.token);
        }
        return () => source.cancel();
    }, [state?.selectedFloorPlan]);

    useEffect(() => {
        if (state?.seats && !isNaN(Number(state?.seats)) && Number(state?.seats) > 0) {
            void onTableSelect(selectedTableId, Number(state?.seats));
        }
    }, [state?.seats]);

    async function getFloorsPlans(cancelToken: CancelToken) {
        const [floorPlansRes, tableElementsRes] = await Promise.all([GetFloorPlans(null, false, cancelToken), GetTableElements(false, cancelToken)]);
        // const res = await GetFloorPlans(null, false, cancelToken);
        if (floorPlansRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken.reason) {
                return;
            }
            dispatch(showError(t("Error getting floor plans")));
            setState(prev => ({ ...prev, floorPlans: prev.floorPlans ?? [] }));
            return;
        }

        if (tableElementsRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting table elements")));
            return;
        }

        const register = JSON.parse(localStorage.getItem("register"));
        const defaultFloorPlanId = register?.default_floor_plan_id;
        const floorPlans = Array.isArray(floorPlansRes?.data) ? floorPlansRes.data : [];
        const preferredFloorPlan =
            floorPlans.find(floorPlan => floorPlan?.id === facilityStore?.selectedFloorPlanId) ??
            floorPlans.find(floorPlan => floorPlan?.id === defaultFloorPlanId || floorPlan?.register_group_id === register?.register_group_id);
        const defaultFloorPlan = preferredFloorPlan ?? floorPlans[0] ?? null;

        let defaultSeats = "1";
        if (guestsAmount && guestsAmount > 0) {
            defaultSeats = guestsAmount?.toString();
        }
        setState(prev => ({...prev, floorPlans, selectedFloorPlan: defaultFloorPlan, seats: defaultSeats, tableElements: tableElementsRes?.data}));
    }

    async function getTables(cancelToken: CancelToken) {
        //loader
        if (state?.tables !== undefined) {
            setState(prev => ({...prev, tables: undefined}));
        }
        const res = await GetTable({floor_plan_id: state?.selectedFloorPlan?.id}, false);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting tables")));
            return;
        }
        const closedTables = res?.data?.filter(table => !table?.open);
        setState(prev => ({...prev, tables: closedTables, selectedTableId: null}));
    }

    function quantityOnBlur() {
        const currentQuantity = Number(state?.seats);
        if (currentQuantity <= 0 || isEmpty(state?.seats)) {
          setState(prev => ({ ...prev, seats: String(1) }));
        }
      }

      function updateQuantity(type: "increment" | "decrement") {
        let currentQuantity = Number(state?.seats);
        if (type === "increment") {
          currentQuantity = !isEmpty(state?.seats) ? ++currentQuantity : 1;
        } else {
          currentQuantity = !isEmpty(state?.seats) ? (currentQuantity > 1 ? --currentQuantity : currentQuantity) : 1;
        }
        setState(prev => ({ ...prev, seats: String(currentQuantity) }));
      }

      function getTableImageSource(table: ITable) {
        const tableElement = state.tableElements.find(tableElement => tableElement.id === table.table_element_id);
        const src = tableElement?.image_src ?? "";
    
        return src;
      }

      function onFloorPlanSelect(floorPlan: IFloorPlan) {
        setState(prev => ({...prev, selectedFloorPlan: floorPlan}));
        void onTableSelect(null, Number(state?.seats));
      }

    return (
        <div className="reservation-table-selection">
            {!state?.floorPlans ? (
                <div style={{display: "flex", justifyContent: "center", gap: "1rem"}}>
                    <Spin/>
                    <div>{t("Loading Table Details...")}</div>
                </div>
            ) : state.floorPlans.length === 0 ? (
                <Callout
                    type="info"
                    title={t("No floor plans")}
                    content={t("There are no floor plans for this facility.")}
                />
            ) : (
        <>
            <div className="reservation-table-selection-controls">
            <FormLayout>
                <FormLayout.Group>
                    <SelectNew 
                        id={"floor_plans"}
                        value={state?.selectedFloorPlan?.id}
                        onChange={(_, extraVal) => onFloorPlanSelect(extraVal)}
                        options={state?.floorPlans?.map(floorPlan => ({ label: floorPlan?.title, id: floorPlan?.id, extraValue: floorPlan }))}
                        placeholder={t("Select floor plan")}
                        isLoading={!state?.floorPlans}
                        label={t("Floor plan")}
                        usePortal
                    />
                    <Input 
                        className="reservation-table-selection-input-text"
                        label={t("Number of seats")}   
                        value={state.seats ?? ""} 
                        onChange={e => setState(prev => ({...prev, seats: e.target.value}))} 
                        type={"number"}
                        min={1}
                        leadingButtons={[<Button key={1} size="medium" onClick={() => updateQuantity("decrement")}><FontAwesomeIcon icon={faMinus} size="1x" /></Button>]}
                        trailingButtons={[<Button key={2} size="medium" onClick={() => updateQuantity("increment")}><FontAwesomeIcon icon={faPlus} size="1x" /></Button>]}
                        onBlur={quantityOnBlur}
                        />
                </FormLayout.Group>
            </FormLayout>
            <p className="reservation-table-selection-table-text">{t("Select a table")}</p>
            </div>
            <div className="reservation-table-selection-tables-scroll">
                <div className="reservation-table-selection-tables-container">
                    {!state?.tables ? (
                        <div style={{display: "flex", justifyContent: "center", gap: "1rem"}}>
                        <Spin/>
                        <div>{t("Loading Tables...")}</div>
                    </div>
                    ) : state?.tables?.length === 0 ? (
                        <Callout type="info" title={t("No Tables Found")} content={t("There are no tables set for this floorplan")} />
                    ) : state?.tables?.map((table, index) => {
                        return (
                            <div key={index} className={classNames("reservation-table-selection-table-card", {
                                "reservation-table-selection-table-card-selected": selectedTableId === table?.id,
                            })} onClick={() => onTableSelect(table?.id, Number(state?.seats))}>
                                <div className="reservation-table-selection-table-left">  
                                    <div className="reservation-table-selection-table-image-container">
                                        <img src={getTableImageSource(table)} alt={t("Table image")} className="reservation-table-selection-table-image" />
                                    </div> 

                                    <div>
                                        <p className="reservation-table-selection-table-primary">{table?.title}</p>
                                        <p className="reservation-table-selection-table-secondary">{table?.user_full_name}</p>
                                    </div>
                                </div>
                                <div className="pointer-events-none">
                                    <Checkbox circle size="medium" checked={table?.id === selectedTableId} onChange={() => onTableSelect(table?.id, Number(state?.seats))} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
            )}
            
        </div>
    );
}